type ChatAuthTokenPayload = {
  token?: string;
  expires_at?: string;
  ttl_seconds?: number;
};

const TOKEN_REFRESH_BUFFER_MS = 5_000;

let cachedToken: string | null = null;
let cachedTokenExpiryMs = 0;
let inflightTokenRequest: Promise<string> | null = null;

const hasValidCachedToken = (): boolean => {
  if (!cachedToken) {
    return false;
  }
  return Date.now() + TOKEN_REFRESH_BUFFER_MS < cachedTokenExpiryMs;
};

const parseTokenExpiryMs = (payload: ChatAuthTokenPayload): number => {
  const expiresAtMs = payload.expires_at ? Date.parse(payload.expires_at) : NaN;
  if (!Number.isNaN(expiresAtMs) && expiresAtMs > 0) {
    return expiresAtMs;
  }

  const ttlSeconds = Number(payload.ttl_seconds ?? 0);
  if (ttlSeconds > 0) {
    return Date.now() + ttlSeconds * 1000;
  }
  return Date.now() + 60_000;
};

export async function getChatAuthToken(forceRefresh: boolean = false): Promise<string> {
  if (!forceRefresh && hasValidCachedToken()) {
    return cachedToken as string;
  }

  if (!forceRefresh && inflightTokenRequest) {
    return inflightTokenRequest;
  }

  inflightTokenRequest = (async () => {
    const response = await fetch('/api/chat/token', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new Error(`Unable to obtain chat auth token (${response.status}): ${errorText}`);
    }

    const payload = (await response.json()) as ChatAuthTokenPayload;
    if (!payload?.token) {
      throw new Error('Chat auth token response did not include a token.');
    }

    cachedToken = payload.token;
    cachedTokenExpiryMs = parseTokenExpiryMs(payload);
    return payload.token;
  })();

  try {
    return await inflightTokenRequest;
  } finally {
    inflightTokenRequest = null;
  }
}

export async function getChatAuthHeaders(): Promise<Record<string, string>> {
  const token = await getChatAuthToken();
  return {
    Authorization: `Bearer ${token}`,
  };
}
